<template>
	<AppDropdown
		:is-loading="loading"
		:has-content="loading || !!result"
		:popover-props="{
			popperClass: 'bookmarks-shortcut-popover',
			theme: 'header-dropdown',
			distance: 23,
		}"
		:trigger-button-props="{
			'v-tooltip': $t('bookmarks.name'),
			'aria-label': $t('bookmarks.name'),
			'data-testid': 'bookmarks-shortcut-trigger',
		}"
		@show="handleDropdownToggled"
	>
		<template #trigger>
			<SuiIcon class="bookmarks-shortcut__icon" name="icon-bookmark-24" />
		</template>

		<BookmarksShortcutSkeleton v-if="loading" />
		<template v-else-if="result">
			<div
				v-if="haveBookmarks"
				class="bookmarks-shortcut-popover__content"
			>
				<SuiListGroup :list-data="bookmarksListGroup" />
			</div>
			<div v-else>
				<SuiStaticMessage
					class="bookmarks-shortcut__empty-message"
					size="compact"
				>
					{{ $t('spa__bookmarks.favoriteBookmarks.info') }}
				</SuiStaticMessage>
			</div>
			<hr />
			<SuiListGroup :list-data="settingsListGroup" />
		</template>
	</AppDropdown>
</template>

<script lang="ts">
import { computed, defineComponent, watch } from 'vue';
import { SuiListGroup, SuiStaticMessage, SuiIcon } from '@developers/scoro-ui';
import type { GroupedList } from '@developers/scoro-ui';
import { Module } from '@/constants/modules';
import { useTranslation, useDelayedLoadingQuery } from '@/composables';
import { useBookmarksStore } from '../../stores/bookmarks';
import { useBookmarksShortcutLazyQuery } from '@/generated/graphql';
import { AppDropdown } from '@/features/App';
import {
	APOLLO_PARAMS_PERSISTED_QUERY,
	usePersistedQueryLoading,
} from '@/lib/apollo';
import BookmarksShortcutSkeleton from './BookmarksShortcutSkeleton.vue';

export default defineComponent({
	components: {
		SuiListGroup,
		SuiStaticMessage,
		BookmarksShortcutSkeleton,
		AppDropdown,
		SuiIcon,
	},

	setup() {
		const { t } = useTranslation();
		const bookmarksStore = useBookmarksStore();
		const { result, load, loading, refetch } = usePersistedQueryLoading(
			useDelayedLoadingQuery(
				useBookmarksShortcutLazyQuery({
					...APOLLO_PARAMS_PERSISTED_QUERY,
				})
			)
		);

		const bookmarksListGroup = computed<GroupedList[]>(() => [
			{
				id: 'group-1',
				items:
					result.value?.FavoriteBookmarks.map((bookmark) => ({
						id: bookmark.id,
						title: bookmark.name,
						icon: { name: 'icon-bookmark-16', classes: [] },
						href: `/${bookmark.moduleUrl}?bookmark_id=${bookmark.id}`,
						target: '',
					})) ?? [],
			},
		]);

		const settingsListGroup = computed<GroupedList[]>(() => [
			{
				id: 'group-1',
				items: [
					{
						id: '1',
						title: t('settings.bookmarks'),
						icon: { name: 'icon-sprocket-16', classes: [] },
						href: `/${Module.SETTINGS.name}/bookmarks`,
						target: '',
					},
				],
			},
		]);

		const handleDropdownToggled = () => load();

		// Monolith will update store when favorite bookmarks are changed
		watch(
			() => bookmarksStore.lastUpdateInMonolith,
			() => refetch()
		);

		return {
			loading,
			result,
			bookmarksListGroup,
			settingsListGroup,
			handleDropdownToggled,
			haveBookmarks: computed(
				() => !!result.value?.FavoriteBookmarks.length
			),
		};
	},
});
</script>

<style lang="scss" scoped>
.bookmarks-shortcut {
	&__icon.sui-icon {
		fill: $neutral-80;
	}

	&__empty-message {
		margin: 1rem 2rem 2rem;
	}
}

.bookmarks-shortcut-popover__content :deep(.list-item) {
	overflow-wrap: break-word;
}
</style>

<style lang="scss">
$dropdown-footer-height: 49px;

.bookmarks-shortcut-popover {
	max-width: 50rem;

	&__content {
		--scroll-bar-size: #{$scroll-bar-small};

		/**
		 * The bookmarks should scroll inside the dropdown so that the last
		 * item (settings link) is always stickied to the bottom.
		 */
		max-height: calc(
			100vh - #{$header-height} - #{$dropdown-footer-height}
		);
		overflow-y: auto;
	}

	.v-popper__inner {
		overflow-y: hidden;
	}
}
</style>
